import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface NewsItem {
  id: string;
  date: string;
  title: string;
  content: string;
  createdAt: number;
}

// 初期データ
const initialNews: NewsItem[] = [
  {
    id: '1',
    date: '2026年3月1日',
    title: 'ホームページを全面リニューアルいたしました',
    content: '2024年3月から作成しておりましたこの心不全早期発見と慢性管理を地域連携で支えるプロジェクトのホームページを全面リニューアルいたしました。より、地域の医療機関の先生方と、患者さんへの切れ目のないサポートを目指して充実させていきたいと思います。',
    createdAt: 1772524800000
  }
];

export default function News() {
  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [password, setPassword] = useState('');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    date: '',
    title: '',
    content: ''
  });

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = () => {
    const stored = localStorage.getItem('heartFailureNews');
    if (stored) {
      const parsed = JSON.parse(stored);
      setNewsList(parsed.sort((a: NewsItem, b: NewsItem) => b.createdAt - a.createdAt));
    } else {
      // 初期データを設定
      localStorage.setItem('heartFailureNews', JSON.stringify(initialNews));
      setNewsList(initialNews);
    }
  };

  const handleAdminLogin = () => {
    if (password === 'cr.toranomon') {
      setIsAdmin(true);
      setPassword('');
    } else {
      alert('パスワードが正しくありません');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isEditing && editingId) {
      // 編集モード
      const updated = newsList.map(item => 
        item.id === editingId 
          ? { ...item, date: formData.date, title: formData.title, content: formData.content }
          : item
      );
      localStorage.setItem('heartFailureNews', JSON.stringify(updated));
      setNewsList(updated);
      setIsEditing(false);
      setEditingId(null);
      alert('お知らせを更新しました');
    } else {
      // 新規投稿モード
      const newItem: NewsItem = {
        id: Date.now().toString(),
        date: formData.date,
        title: formData.title,
        content: formData.content,
        createdAt: Date.now()
      };
      const updated = [newItem, ...newsList];
      localStorage.setItem('heartFailureNews', JSON.stringify(updated));
      setNewsList(updated);
      alert('お知らせを投稿しました');
    }
    
    setFormData({ date: '', title: '', content: '' });
  };

  const handleEdit = (news: NewsItem) => {
    setIsEditing(true);
    setEditingId(news.id);
    setFormData({
      date: news.date,
      title: news.title,
      content: news.content
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData({ date: '', title: '', content: '' });
  };

  const handleDelete = (id: string) => {
    if (window.confirm('このお知らせを削除しますか？')) {
      const updated = newsList.filter(item => item.id !== id);
      localStorage.setItem('heartFailureNews', JSON.stringify(updated));
      setNewsList(updated);
      if (selectedNews?.id === id) {
        setSelectedNews(null);
      }
    }
  };

  const recentNews = newsList.slice(0, 3);
  const olderNews = newsList.slice(3);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">お知らせ</h1>
            <p className="text-lg text-gray-600">プロジェクトの最新情報をお届けします</p>
          </div>

          {!isAdmin && (
            <div className="mb-8 text-right">
              <button
                onClick={() => setShowAdminPanel(!showAdminPanel)}
                className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                管理者ログイン
              </button>
            </div>
          )}

          {showAdminPanel && !isAdmin && (
            <div className="mb-12 bg-gray-50 rounded-lg p-6 max-w-md mx-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">管理者ログイン</h3>
              <div className="flex gap-3">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="パスワードを入力"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
                />
                <button
                  onClick={handleAdminLogin}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap text-sm"
                >
                  ログイン
                </button>
              </div>
            </div>
          )}

          {isAdmin && (
            <div className="mb-12 bg-teal-50 rounded-lg p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {isEditing ? 'お知らせを編集' : '新しいお知らせを投稿'}
                </h3>
                <button
                  onClick={() => setIsAdmin(false)}
                  className="text-sm text-gray-600 hover:text-gray-800 cursor-pointer"
                >
                  ログアウト
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    日付
                  </label>
                  <input
                    type="text"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    placeholder="例：2024年12月"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    タイトル
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="例：当チームの山田が日本循環器学会で発表いたしました"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    詳細内容
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="発表の詳細内容を入力してください"
                    required
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap"
                  >
                    {isEditing ? '更新する' : '投稿する'}
                  </button>
                  {isEditing && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium whitespace-nowrap"
                    >
                      キャンセル
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}

          {newsList.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-information-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">まだお知らせはありません</p>
            </div>
          ) : (
            <>
              {recentNews.length > 0 && (
                <div className="mb-16">
                  <h2 className="text-2xl font-bold text-gray-900 mb-8 pb-3 border-b-2 border-teal-600">
                    最新のお知らせ
                  </h2>
                  <div className="space-y-4">
                    {recentNews.map((news) => (
                      <div
                        key={news.id}
                        className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full whitespace-nowrap">
                                {news.date}
                              </span>
                              <span className="inline-block px-3 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full whitespace-nowrap">
                                NEW
                              </span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">
                              {news.title}
                            </h3>
                            <button
                              onClick={() => setSelectedNews(news)}
                              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm cursor-pointer"
                            >
                              詳細を見る
                              <i className="ri-arrow-right-line"></i>
                            </button>
                          </div>
                          {isAdmin && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEdit(news)}
                                className="text-teal-600 hover:text-teal-700 cursor-pointer p-2"
                              >
                                <i className="ri-edit-line text-xl"></i>
                              </button>
                              <button
                                onClick={() => handleDelete(news.id)}
                                className="text-red-500 hover:text-red-700 cursor-pointer p-2"
                              >
                                <i className="ri-delete-bin-line text-xl"></i>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {olderNews.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-8 pb-3 border-b-2 border-gray-300">
                    過去のお知らせ
                  </h2>
                  <div className="space-y-3">
                    {olderNews.map((news) => (
                      <div
                        key={news.id}
                        className="bg-gray-50 rounded-lg p-5 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-sm text-gray-600 whitespace-nowrap">
                                {news.date}
                              </span>
                            </div>
                            <h3 className="text-base font-medium text-gray-900 mb-2">
                              {news.title}
                            </h3>
                            <button
                              onClick={() => setSelectedNews(news)}
                              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 text-sm cursor-pointer"
                            >
                              詳細を見る
                              <i className="ri-arrow-right-line"></i>
                            </button>
                          </div>
                          {isAdmin && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEdit(news)}
                                className="text-teal-600 hover:text-teal-700 cursor-pointer p-2"
                              >
                                <i className="ri-edit-line text-lg"></i>
                              </button>
                              <button
                                onClick={() => handleDelete(news.id)}
                                className="text-red-500 hover:text-red-700 cursor-pointer p-2"
                              >
                                <i className="ri-delete-bin-line text-lg"></i>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {selectedNews && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50"
          onClick={() => setSelectedNews(null)}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex justify-between items-start">
              <div className="flex-1 pr-4">
                <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full mb-3 whitespace-nowrap">
                  {selectedNews.date}
                </span>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedNews.title}
                </h2>
              </div>
              <button
                onClick={() => setSelectedNews(null)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer p-2"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="px-8 py-6">
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedNews.content}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
